import Vue from 'vue';
import { calmComputed, newFunction } from "../../lib/utils";
import { INJECT_UICORE_BUS, INJECT_UICORE_RENDERER } from "../../lib/utils/consts";
import { formOverridePropsProvider } from "../../lib/builtin/plugins/form/formOverrideProps";
import { getObjectUid } from "../../lib/utils/objectUidMemory";
import { isEqual } from "lodash-es";
const isValidKey = val => (typeof val === 'string' || typeof val === 'number') && val !== '';

/**
 * 这是用来方便开发列表型组件的，适用于 “卡片列表”、“Tab列表” 等渲染 Array 的组件。
 *
 * 这里有很多的约定，请仔细阅读：
 *
 * 1. 这个 mixin 会提供若干 props，在组件里可以不声明，但是仍然需要在 meta 里补充：
 *
 *    ```js
 *    meta补充 = {
 *      isFormEditor: true,
 *      bindings: [
 *        {
 *          id: '数据源',
 *          pathProp: 'path',
 *          required: true,
 *          main: true,
 *          checkData: s => s.type === 'array',
 *        },
 *      ],
 *      slots: [
 *        { id: 'item', type: 'scoped' },
 *      ]
 *      propsSchema: [
 *        { id: 'path', type: 'string', title: '数据源' },
 *        { id: 'readonly', type: 'boolean', title: '只读模式', default: false },
 *      ]
 *    }
 *    ```
 *
 * 2. 开发时使用这些 mixin 进来的 computed
 *
 *    - `array` - (可赋值修改) 当前操作的数组实例
 *    - `arrayLength`   - 数组长度
 *    - `finalReadonly` - 是否真的只读，会综合考虑 prop 和 stub 的状态（因为 readonly 这个名字已经被 props 占了，所以这里换了个名字）
 *    - `itemRendererData`  - 用来渲染子页面片的，下面有例子
 *
 * 3. 你的组件可调用这些方法
 *
 *    - `this.addItem()` 数组里添加一项
 *    - `this.deleteItem(idx)` 数组里删除一项
 *
 * ## 子页面片
 *
 * 如果采用子页面片的方式，渲染时大概像这样：
 *
 * ### 组件里
 *
 * 注意需要额外自己补充声明 `pageletId` 的 prop 或者 data
 *
 * ```xml
 * <div
 *   v-if="emptySlotShown"
 *   class=""
 * >
 *   哦，暂时没有内容
 * </div>
 * <div
 *    v-else
 *    class="myArrayList"
 * >
 *   <div
 *     class="myArrayItem"
 *     ref="arrayItemContainers"
 *     v-for="({ slotProps, index, key }) in itemRendererData"
 *     :data-index="index"
 *     :key="key"
 *   >
 *     <slot
 *       name="item"
 *       v-bind="slotProps"
 *     />
 *   </div>
 * </div>
 * ```
 *
 * 然后在渲染的时候，对应的槽位会变成可编辑的“作用域插槽”
 */
export const listComponentMixin = Vue.extend({
  props: {
    path: {
      type: String,
      default: ''
    },
    keyingRule: {
      type: null,
      default: null
    },
    readonly: {
      type: [Boolean, String],
      default: false
    },
    onDelete: {
      type: Function,
      default: null
    }
  },
  mixins: [formOverridePropsProvider],
  data() {
    return {
      emulateEmptyMode: false,
      itemRendererData: [],
      itemKeyGetter: (item, index) => `$_${index}`,
      [tempArrayCache]: null
    };
  },
  inject: {
    uc: {
      from: INJECT_UICORE_BUS,
      default: null
    },
    renderer: {
      from: INJECT_UICORE_RENDERER,
      default: null
    }
  },
  created() {
    this.$watch(() => {
      var _this$arrayStub;
      return (_this$arrayStub = this.arrayStub) === null || _this$arrayStub === void 0 ? void 0 : _this$arrayStub.value;
    }, value => {
      this.$data[tempArrayCache] = value;
    }, {
      immediate: true
    });
    this.$watch(() => {
      var _this$arrayStub2;
      const primaryKey = ((_this$arrayStub2 = this.arrayStub) === null || _this$arrayStub2 === void 0 || (_this$arrayStub2 = _this$arrayStub2.getChildStub(0)) === null || _this$arrayStub2 === void 0 ? void 0 : _this$arrayStub2.primaryKey) || '';
      return {
        primaryKey,
        rule: this.keyingRule
      };
    }, (v, lv) => {
      if (isEqual(v, lv)) return;
      const {
        primaryKey,
        rule
      } = v;

      // eslint-disable-next-line operator-linebreak
      const fn =
      // 自定义keying
      (rule === null || rule === void 0 ? void 0 : rule.type) === 'function' && newFunction(['renderer', 'item', 'index'], `return (${rule.code}\n).call(renderer, item, index)`);
      this.itemKeyGetter = (item, index) => {
        if (typeof fn === 'function') {
          try {
            const result = fn(this.renderer, item, index);
            if (isValidKey(result)) return result;
          } catch (e) {
            // TODO: 要不要输出错误
          }
        }

        // ------
        // fallback 到默认的 keying 逻辑

        const presetKey = primaryKey && item ? item[primaryKey] : null;
        if (isValidKey(presetKey)) return presetKey;
        return getObjectUid(item) || `__${index}`;
      };
    }, {
      immediate: true
    });

    // 自动重建 itemRendererData 的逻辑
    this.$watch(() => [this.arrayLength, this.itemRendererSchema], ([newArrayLength, itemRendererSchema], [, /* arrayLength */lastItemRendererSchema] = []) => {
      // ------------------------
      // 如果数组结构发生变化了，那么整个 itemRendererData 是要重建的
      const needForceRebuild = itemRendererSchema !== lastItemRendererSchema;
      const oldData = needForceRebuild ? [] : this.itemRendererData;
      const offset = oldData.length;

      // ------------------------
      const listComponent = this;
      let renderCount = newArrayLength;
      if (this.isDesignMode && renderCount > 10) renderCount = 10;
      if (offset === renderCount) return; // nothing to change

      if (offset > renderCount) {
        // 禁止 Vue 在列表组件这一层，就监听了整个数组
        this.itemRendererData = Object.seal(oldData.slice(0, renderCount));
        return;
      }

      // eslint-disable-next-line no-param-reassign
      const answer = oldData.concat(Array.from({
        length: renderCount - offset
      }, (_, offset2) => {
        const index = offset + offset2;
        return {
          index,
          get key() {
            const {
              itemKeyGetter,
              array
            } = listComponent;
            const item = array[index];
            return itemKeyGetter(item, index);
          },
          slotProps: {
            dataSchema: itemRendererSchema,
            data: {
              index,
              num: index + 1,
              get item() {
                return listComponent.array[index];
              },
              set item(v) {
                listComponent.arrayStub.set(index, v);
              },
              get list() {
                return listComponent.array;
              }
            }
          }
        };
      }));

      // 禁止 Vue 在列表组件这一层，就监听了整个数组
      this.itemRendererData = Object.seal(answer);
    }, {
      immediate: true
    });
  },
  computed: {
    isDesignMode() {
      var _this$uc;
      return !!((_this$uc = this.uc) !== null && _this$uc !== void 0 && _this$uc.isDesignMode);
    },
    arrayStub() {
      const ans = this.renderer.getStubAtPath(this.path);
      if ((ans === null || ans === void 0 ? void 0 : ans.type) !== 'array') return null;
      return ans;
    },
    array: {
      get() {
        if (this.emulateEmptyMode) return [];
        return this.$data[tempArrayCache];
      },
      set(newVal) {
        if (this.emulateEmptyMode) return;
        this.arrayStub.value = newVal;
      }
    },
    arrayLength: calmComputed(function () {
      const {
        array
      } = this;
      if (!Array.isArray(array)) return 0;
      return this.array.length;
    }),
    finalReadonly: calmComputed(function () {
      var _this$arrayStub3;
      return !!(this.readonly || (_this$arrayStub3 = this.arrayStub) !== null && _this$arrayStub3 !== void 0 && _this$arrayStub3.readonly);
    }),
    itemSchema: calmComputed(function () {
      var _this$arrayStub4;
      return (_this$arrayStub4 = this.arrayStub) === null || _this$arrayStub4 === void 0 ? void 0 : _this$arrayStub4.arrItems;
    }),
    arraySchema: calmComputed(function () {
      var _this$arrayStub5;
      return (_this$arrayStub5 = this.arrayStub) === null || _this$arrayStub5 === void 0 ? void 0 : _this$arrayStub5.schema;
    }),
    itemRendererSchema() {
      const {
        itemSchema,
        arraySchema
      } = this;
      return {
        type: 'object',
        title: '当前列表项',
        fields: [{
          ...itemSchema,
          id: 'item',
          title: '列表项数据'
        }, {
          id: 'num',
          type: 'integer',
          title: '序号（从1开始）',
          readonly: true
        }, {
          id: 'index',
          type: 'integer',
          title: '序号（从0开始）',
          readonly: true
        }, {
          ...arraySchema,
          id: 'list',
          title: '所属的列表',
          readonly: true
        }]
      };
    },
    emptySlotShown() {
      return this.emulateEmptyMode || !this.arrayLength;
    }
  },
  methods: {
    addItem(item) {
      let newItem = typeof item === 'undefined' ? this.arrayStub.getChildStub(this.arrayLength).getDefaultValue() : item;
      const add = () => {
        const array = [...(this.arrayStub.value || [])];
        array.push(newItem);
        this.arrayStub.value = array;
      };
      // 拉起事件 然后获取新的 item
      if (typeof this.handleAdd === 'function') {
        return this.handleAdd(newItem).then(res => {
          if (res !== null && res !== void 0 && res.res) {
            newItem = res.res;
          }
          add();
        });
      }
      return add();
    },
    insertBefore(index, item) {
      const newItem = typeof item === 'undefined' ? this.arrayStub.getChildStub(this.arrayLength).getDefaultValue() : item;
      const array = [...(this.arrayStub.value || [])];
      array.splice(index, 0, newItem);
      this.arrayStub.value = array;
    },
    async handleDeleteEvent(idx) {
      if (typeof this.onDelete !== 'function') return;
      try {
        const eventArg = {
          index: idx,
          get value() {
            return this.arrayStub.value[idx];
          }
        };
        Object.defineProperty(eventArg, 'array', {
          get: () => this.arrayStub.value
        });
        await this.onDelete(eventArg);
      } catch (e) {
        return 'abort';
      }
    },
    async deleteItem(idx) {
      const eventRes = await this.handleDeleteEvent(idx);
      if (eventRes === 'abort') return;
      const copy = this.arrayStub.value.slice();
      copy.splice(idx, 1);
      this.arrayStub.value = copy;
    }
  },
  ucMethods(decls) {
    decls.push({
      id: 'addItem',
      title: '添加一项(到末尾)',
      arguments: [{
        id: 'item',
        type: 'any',
        title: '要添加的数据'
      }]
    }, {
      id: 'insertBefore',
      title: '添加一项(插入到指定位置)',
      arguments: [{
        id: 'index',
        type: 'number',
        title: '插入位置(从0开始)'
      }, {
        id: 'item',
        type: 'any',
        title: '要添加的数据'
      }]
    }, {
      id: 'deleteItem',
      title: '删除一项',
      arguments: [{
        id: 'index',
        type: 'number',
        title: '要删除项的序号（从0开始）',
        description: '在卡片、表格等组件的格子中时，通常取 `data.index`'
      }]
    });
  }
});
const tempArrayCache = '$_listComponentMixin_array_doNotUseThis';